import styled from "styled-components";
import moment from "moment-timezone";
import { RATypography, RATextInput, RACheckbox, RAImage, RAButtonPrimary, RADatePicker } from "@/components/customs";
import { RAInputPassenger } from "@/components/atoms";
import { Row, Col, Divider, Grid } from "antd";
import { IconBringOversizeLuggage, IconBringPets } from "@/assets/images";
import { SvgCalendar, SvgProfile } from "@/assets/icons";
import { DATETIME_FORMAT_SHORT } from "@/config/constants";
import { InputDateInquiry } from "./InputDateInquiry";
const { useBreakpoint } = Grid;

export function FormSendInquiry({ data = {}, onChange, onSubmit, error = {}, loading = false, booking = {} }) {
	const {
		additionalNote = "",
		additionalInfo = {},
		departureDatetime,
		passengers,
		allowInputDepartureDatetime = false,
	} = data;
	const screens = useBreakpoint();
	// const formatDTFull = useSelector(selectFormatDateTimeFull)
	const _renderPassengers = () => {
		return (
			<WrapperNote>
				<RATypography variant="body3" color="#595959">
					Passengers
				</RATypography>
				{screens.md ? (
					<RATextInput
						prefix={<SvgProfile color="#595959" />}
						type="number"
						placeholder="Passengers"
						min={1}
						value={passengers}
						onChange={(val) =>
							onChange({
								...data,
								passengers: val,
							})
						}
						error={error?.passengers}
						data-testid="sendInquiry_passengers"
					/>
				) : (
					<RAInputPassenger
						style={{ width: "100%" }}
						size="large"
						value={passengers}
						min={1}
						onChange={(val) =>
							onChange({
								...data,
								passengers: val,
							})
						}
						error={error?.passengers}
						data-testid="sendInquiry_passengers"
					/>
				)}
			</WrapperNote>
		);
	};

	const _renderDepartureTime = () => {
		const getTitleWithDepartureWindow = () => {
			let title = "Preferred Departure Time";
			if (booking?.departure_window && booking?.departure_window?.utc_datetime) {
				title = `${title} (From: ${moment(booking?.departure?.utc_datetime)
					.tz(booking?.from_airport?.location?.timezone)
					.format(DATETIME_FORMAT_SHORT)} - To: ${moment(booking?.departure_window?.utc_datetime)
					.tz(booking?.from_airport?.location?.timezone)
					.format(DATETIME_FORMAT_SHORT)})`;
			} else if (!booking?.departure?.utc_datetime && booking?.empty_leg?.departure?.utc_datetime) {
				if (booking?.empty_leg?.departure_window?.utc_datetime) {
					title = `${title} (From: ${moment(booking?.empty_leg?.departure?.utc_datetime)
						.tz(booking?.from_airport?.location?.timezone)
						.format(DATETIME_FORMAT_SHORT)} - To: ${moment(
						booking?.empty_leg?.departure_window?.utc_datetime,
					)
						.tz(booking?.from_airport?.location?.timezone)
						.format(DATETIME_FORMAT_SHORT)})`;
				} else {
					title = `${title} (${moment(booking?.empty_leg?.departure?.utc_datetime)
						.tz(booking?.from_airport?.location?.timezone)
						.format(DATETIME_FORMAT_SHORT)})`;
				}
			} else {
				title = "Departure Time";
			}
			return title;
		};
		const getDisabledFromDate = () => {
			if (!booking?.departure?.utc_datetime && booking?.empty_leg?.departure?.utc_datetime) {
				return booking?.empty_leg?.departure?.utc_datetime;
			} else {
				return null;
			}
		};
		return (
			<WrapperNote>
				<RATypography variant="body3" color="#595959">
					{getTitleWithDepartureWindow()}
				</RATypography>
				<InputDateInquiry
					departure_datetime={departureDatetime}
					disabled={!allowInputDepartureDatetime}
					disabledFromDate={getDisabledFromDate()}
					onChange={(val) =>
						onChange({
							...data,
							departureDatetime: val,
						})
					}
					error={error}
				/>
			</WrapperNote>
		);
	};

	const _renderNote = () => {
		return (
			<WrapperNote>
				<RATypography variant="body3" color="#595959">
					Additional Requests
				</RATypography>
				<RATextInput
					type="textarea"
					placeholder="Enter your additional requests"
					value={additionalNote}
					onChange={(val) => onChange({ ...data, additionalNote: val })}
					data-testid="sendInquiry_note"
				/>
			</WrapperNote>
		);
	};

	const _renderAdditionalServices = () => {
		return (
			<WrapperAdditionalServices>
				<RequestAdditionalServices
					title="I’m bringing a pet"
					description="Please provide details so we can ensure a comfortable journey for your furry friend."
					active={additionalInfo?.is_bring_pet}
					onChange={() => {
						let value = !additionalInfo?.is_bring_pet;
						onChange({
							...data,
							additionalInfo: {
								...data?.additionalInfo,
								is_bring_pet: value,
							},
						});
					}}
					icon={IconBringPets}
					data-testid="sendInquiry_bringPetCheckbox"
				/>
				<RequestAdditionalServices
					title="I’m bringing oversized luggage"
					description="Let us know the dimensions, so we can accommodate your larger items seamlessly."
					active={additionalInfo?.is_bring_oversize_luggage}
					onChange={() => {
						let value = !additionalInfo?.is_bring_oversize_luggage;
						onChange({
							...data,
							additionalInfo: {
								...data?.additionalInfo,
								is_bring_oversize_luggage: value,
							},
						});
					}}
					icon={IconBringOversizeLuggage}
					data-testid="sendInquiry_oversizeLuggageCheckbox"
				/>
			</WrapperAdditionalServices>
		);
	};

	return (
		<ContainerRequestFlight data-testid="sendInquiryModal_content">
			<WrapperContent>
				<RATypography variant="h3">Send Inquiry</RATypography>
			</WrapperContent>
			<Divider />
			{_renderPassengers()}
			{_renderDepartureTime()}
			{_renderNote()}
			{_renderAdditionalServices()}
			<Row gutter={[20, 20]} justify="center" align="middle" style={{ marginTop: "40px" }}>
				<ButtonCol>
					<RAButtonPrimary
						title="SUBMIT"
						onClick={() => onSubmit()}
						width="auto"
						loading={loading}
						data-testid="sendInquiry_submitBtn"
					/>
					{error?.submit_failed ? (
						<RATypography variant="body3" color="#BF1A2F" top="10px" align="center">
							{error?.submit_failed}
						</RATypography>
					) : null}
				</ButtonCol>
			</Row>
		</ContainerRequestFlight>
	);
}

const RequestAdditionalServices = ({
	active,
	onChange,
	icon,
	title = "",
	description = "",
	"data-testid": dataTestId = null,
}) => (
	<WrapperOptionItem
		onClick={(e) => {
			e?.preventDefault();
			e?.stopPropagation();
			onChange(!active);
		}}
		active={active}
		data-testid={dataTestId}
	>
		<RACheckbox checked={active} />
		<RAImage width="48px" height="48px" url={icon} />
		<div>
			<RATypography variant="sub-heading4" color="#222222">
				{title}
			</RATypography>
			<RATypography variant="body3" color="#595959" top="4px">
				{description}
			</RATypography>
		</div>
	</WrapperOptionItem>
);

const ContainerRequestFlight = styled.div`
	width: "100%";
`;
const WrapperContent = styled.div`
	padding: 0px 20px;
	flex-direction: column;
	align-items: center;
	border-left: 2px solid #ffb11a;
`;
const WrapperNote = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin-bottom: 20px;
`;
const WrapperAdditionalServices = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;
const WrapperOptionItem = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: 16px 20px;
	gap: 12px;
	width: 100%;
	min-height: 80px;
	border-radius: 10px;
	border: 1px solid #e9e8e8;
	cursor: pointer;
	${({ active }) =>
		active &&
		`
        border: 1px solid #FFB11A;
    `}
`;
const ButtonCol = styled(Col)`
	display: flex;
	flex-direction: column;
	align-items: center;
`;
